import { Component, ViewEncapsulation, HostBinding, OnDestroy, Input } from '@angular/core';
import {
	Subscription,
	fromEvent,
	of,
	forkJoin,
	Observable,
	BehaviorSubject,
	PartialObserver,
	switchMap,
	map
} from 'rxjs';
import {
	GridReadyEvent,
	ICellRendererParams,
	GridApi,
	IServerSideDatasource,
	IServerSideGetRowsParams,
	ViewportChangedEvent,
} from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { CommonGridOptions, serverSideAgGridModules } from '../../../../shared/datacenter/grid-options';
import { OfferService } from '../../../services/offer.api.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { createOfferPeriod } from '../../cards/offer-header-card-block/create-offer-period';
import { formatNumber, formatTextDimension } from '../../../../shared/utils/number.format';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { OfferLineListFilter } from '../../offer-list/offer-line-list-filter';
import { OfferStatus } from '../../../models/offer';
import { OfferLine } from '../../../models/offer-line';
import { UNDERSCORE, OrderByDirection } from '../../../../shared/utils/constant';
import { CommonColDef, LinkColDef, NumericColDef, DateColDef } from '../../../../shared/datacenter/column-defs';
import { safe } from '../../../../shared/utils/safe';
import { ColDefWithFields } from '../../../../shared/datacenter/column-type';

import {
	compareIdNbrStr,
	compareStr,
	comparePeriod,
	compareDate,
	compareOfferStatus,
	compareNbr,
	compareDimension
} from './comparator-utils';

const LIMIT = 1000;
const resizeObservable = fromEvent(globalThis, 'resize');

const linkToOfferCardCellRendererParams = {
	routerLink(params: ICellRendererParams<OfferLine, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const offerLine = params.data;
		return {
			commands: [
				'/offer',
				'card',
				offerLine.header.id ? `e${ offerLine.header.id }` : `c${ safe(offerLine.header.externalId) }`
			]
		};
	}
};

const linkToProductCardCellRendererParams = {
	routerLink(params: ICellRendererParams<OfferLine, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const offerLine = params.data;
		if (!offerLine.product?.description) {
			return {};
		}
		const { description } = offerLine.product;
		return {
			commands: [
				'/offer',
				'product',
				description.configurationRef || description.productRef,
				description.configurationRef ? 'rtpc' : 'a2c'
			]
		};
	}
};

type ColDefWithCustomComparator<TData = unknown, TValue = unknown> = ColDefWithFields<TData, TValue>
	& { customComparator: (value1: TData, value2: TData) => number };

class CommonColDefWithCustomComparator<TData = unknown, TValue = unknown> extends CommonColDef<TData, TValue> {
	customComparator: (value1: TData, value2: TData) => number;

	constructor(
		colDef: ColDefWithCustomComparator<TData, TValue>
	) {
		super(colDef);
		this.customComparator = colDef.customComparator;
	}
}

class LinkColDefWithCustomComparator<TData = unknown, TValue = unknown> extends LinkColDef<TData, TValue> {
	customComparator: (value1: TData, value2: TData) => number;

	constructor(
		colDef: ColDefWithCustomComparator<TData, TValue>
	) {
		super(colDef);
		this.customComparator = colDef.customComparator;
	}
}

class DateColDefWithCustomComparator<TData = unknown, TValue = unknown> extends DateColDef<TData, TValue> {
	customComparator: (value1: TData, value2: TData) => number;

	constructor(
		colDef: ColDefWithCustomComparator<TData, TValue>
	) {
		super(colDef);
		this.customComparator = colDef.customComparator;
	}
}

class NumericColDefWithCustomComparator<TData = unknown, TValue = unknown> extends NumericColDef<TData, TValue> {
	customComparator: (value1: TData, value2: TData) => number;

	constructor(
		colDef: ColDefWithFields<TData, TValue> & { customComparator: (value1: TData, value2: TData) => number }
	) {
		super(colDef);
		this.customComparator = colDef.customComparator;
	}
}

class BocColDef extends CommonColDefWithCustomComparator<OfferLine> {
	static GET_BOC_LABEL(
		bocFieldName: 'soldTo' | 'shipTo' | 'endUser',
		offerLine: OfferLine,
		offerLineDatasource?: OfferLineDatasource
	) {
		const bocFieldValue = offerLine.header[bocFieldName];
		const boc = bocFieldValue && offerLineDatasource
			? offerLineDatasource.bocs.get(bocFieldValue)
			: undefined;
		return boc ? `${ boc.name } / ${ boc.city }` : UNDERSCORE;
	}

	constructor(
		bocFieldName: 'soldTo' | 'shipTo' | 'endUser',
		translationService: TranslationService,
		table: OfferLinesTableComponent
	) {
		super({
			headerName: translationService.getTR(`eoffer.${ bocFieldName.toLowerCase() }`),
			minWidth: 200,
			valueGetter: params => {
				if (!params.data) {
					return '';
				}
				const offerLine = params.data;
				return BocColDef.GET_BOC_LABEL(bocFieldName, offerLine, table.offerLineDatasource);
			},
			customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => {
				const s1 = BocColDef.GET_BOC_LABEL(bocFieldName, offerLine1, table.offerLineDatasource);
				const s2 = BocColDef.GET_BOC_LABEL(bocFieldName, offerLine2, table.offerLineDatasource);
				return s1.localeCompare(s2);
			}
		});
	}
}

class OfferLineDatasource implements IServerSideDatasource {
	filter?: OfferLineListFilter;
	readonly bocs = new Map<string, BocClient>();
	private sub?: Subscription;
	private rows: OfferLine[] | undefined;

	private readonly rowsCountSubject = new BehaviorSubject(<number | undefined> undefined);

	constructor(
		readonly offerService: OfferService,
		readonly gridApi: GridApi<unknown>,
		readonly type: 'draftOnly' | 'noDraft' | 'all'
	) {}

	subscribeToRowsCount(observer: PartialObserver<number | undefined>) {
		return this.rowsCountSubject.subscribe(observer);
	}

	destroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}

	getRows(params: IServerSideGetRowsParams<OfferLine>) {
		if (this.rows) {
			const filteredRows = this.filterRows(this.rows);
			this.sortRows(filteredRows, <{ colId: string; sort: OrderByDirection }[]> params.request.sortModel);
			this.rowsCountSubject.next(filteredRows.length);
			params.success({ rowData: filteredRows, rowCount: filteredRows.length });
			return;
		}
		if (this.sub) {
			this.sub.unsubscribe();
		}

		this.sub = this.offerService.getOfferLinePage(
			this.filter?.textFilter,
			this.type === 'draftOnly'
				? ['DRAFT']
				: this.type === 'noDraft'
					? ['REQUESTED', 'OFFERED', 'ACCEPTED']
					: undefined,
			undefined,
			undefined,
			'INLINE'
		)
			.pipe(switchMap(
				paginatedResult => forkJoin([of(paginatedResult), this.loadBocs(paginatedResult.results)])
			))
			.subscribe({
				next: ([paginatedResult, bocs]) => {
					for (const boc of bocs) {
						this.bocs.set(String(boc.businessId), boc);
					}
					this.rows = paginatedResult.results;
					const filteredRows = this.filterRows(this.rows);
					this.rowsCountSubject.next(filteredRows.length);
					const lastRow = params.request.endRow === undefined || filteredRows.length <= params.request.endRow
						? filteredRows.length
						: -1;
					params.success({ rowData: filteredRows, rowCount: lastRow });
				},
				error: (err: unknown) => {
					console.error('error requesting datacenter', err);
					this.rowsCountSubject.next(undefined);
					params.fail();
				}
			});
	}

	private filterRows(rows: OfferLine[]) {
		function testTextFilter(offerLine: OfferLine, textFilter: string) {
			return offerLine.header.rfqRef?.toLowerCase().includes(textFilter)
				|| offerLine.product?.description?.steelGrade.toLowerCase().includes(textFilter);
		}

		function testStatusFilter(offerLine: OfferLine, statusFilter: OfferStatus[]) {
			return statusFilter.includes(offerLine.product === undefined
				? 'DRAFT'
				: offerLine.product.offerStatus);
		}

		const { filter } = this;
		if (
			filter === undefined
			|| (filter.statusFilter === undefined && filter.textFilter === undefined)
		) {
			return rows;
		}

		return rows.filter(
			offerLine =>
				(filter.textFilter === undefined || testTextFilter(offerLine, filter.textFilter))
				&& (filter.statusFilter === undefined || testStatusFilter(offerLine, filter.statusFilter))
		);
	}

	private sortRows(rows: OfferLine[], sortModel: { colId: string; sort: OrderByDirection }[]) {
		rows.sort((offerLine1, offerLine2) => {
			for (const sortModelEntry of sortModel) {
				const colDef = <ColDefWithFields | ColDefWithCustomComparator | undefined>
					this.gridApi.getColumnDef(sortModelEntry.colId);
				if (!colDef || !('customComparator' in colDef)) {
					continue;
				}
				const comparatorValue = colDef.customComparator(offerLine1, offerLine2);
				if (comparatorValue !== 0) {
					return sortModelEntry.sort === OrderByDirection.DESC ? comparatorValue : -comparatorValue;
				}
			}
			return 0;
		});
	}

	private loadBocs(offerLines: OfferLine[]) {
		const bocIds: string[] = [];
		for (const offerLine of offerLines) {
			if (
				offerLine.header.soldTo
				&& !bocIds.includes(offerLine.header.soldTo)
			) {
				bocIds.push(offerLine.header.soldTo);
			}
			if (
				offerLine.header.shipTo
				&& !bocIds.includes(offerLine.header.shipTo)
			) {
				bocIds.push(offerLine.header.shipTo);
			}
			if (
				offerLine.header.endUser
				&& !bocIds.includes(offerLine.header.endUser)
			) {
				bocIds.push(offerLine.header.endUser);
			}
		}
		return bocIds.length === 0
			? of([])
			: this.offerService.getBocs(bocIds).pipe(map(res => res.results));
	}
}

@Component({
	selector: 'copo-offer-lines-table',
	templateUrl: './offer-lines-table.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [AgGridModule]
})
export class OfferLinesTableComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() filterObs?: Observable<OfferLineListFilter>;
	@Input() type!: 'draftOnly' | 'noDraft' | 'all';

	gridOptions: CommonGridOptions<OfferLine>;

	exportInProgress = false;
	offerLineCount: number | undefined;
	offerLineDatasource?: OfferLineDatasource;
	readonly agGridModules = serverSideAgGridModules;
	private resizeSub?: Subscription;
	private dataSub?: Subscription;
	private filterSub: Subscription | undefined;

	constructor(
		private readonly offerService: OfferService,
		private readonly translationService: TranslationService
	) {
		this.gridOptions = new CommonGridOptions<OfferLine>({
			translationService: this.translationService,
			rowModelType: 'serverSide',
			multiSortKey: 'ctrl',
			cacheBlockSize: LIMIT,
			columnDefs: [
				// header part
				new LinkColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('application.procurement.supplier.rfqNumber'),
					minWidth: 200,
					pinned: 'left',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						return offerLine.header.id || offerLine.header.referenceAm;
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareIdNbrStr(
						offerLine1.header.id,
						offerLine1.header.referenceAm,
						offerLine2.header.id,
						offerLine2.header.referenceAm
					),
					cellRendererParams: linkToOfferCardCellRendererParams
				}),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('offers.create.details.customer.offer-name'),
					minWidth: 150,
					pinned: 'left',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						return offerLine.header.rfqRef || UNDERSCORE;
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareStr(
						offerLine1.header.rfqRef,
						offerLine2.header.rfqRef
					)
				}),
				new BocColDef('soldTo', this.translationService, this),
				new BocColDef('shipTo', this.translationService, this),
				new BocColDef('endUser', this.translationService, this),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.period'),
					minWidth: 200,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						return createOfferPeriod(offerLine.header);
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => comparePeriod(
						offerLine1.header.periodStartMonth,
						offerLine1.header.periodEndMonth,
						offerLine2.header.periodStartMonth,
						offerLine2.header.periodEndMonth
					)
				}),
				new DateColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.validityDate'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						return offerLine.header.validityDate;
					},
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareDate(
						offerLine1.header.validityDate,
						offerLine2.header.validityDate
					)
				}),
				// line data
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.offerLine'),
					minWidth: 200,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product) {
							return undefined;
						}
						const { product } = offerLine;
						return product.id || product.referenceAm;
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => {
						const p1 = offerLine1.product;
						const p2 = offerLine2.product;
						return compareIdNbrStr(
							p1 ? p1.id : undefined,
							p1 ? p1.referenceAm : undefined,
							p2 ? p2.id : undefined,
							p2 ? p2.referenceAm : undefined
						);
					}
				}),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.status'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product) {
							return this.translationService.getTR('eoffer.status.draft');
						}
						return this.translationService.getTR(
							`eoffer.status.${ offerLine.product.offerStatus.toLowerCase() }`
						);
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareOfferStatus(
						offerLine1.product ? offerLine1.product.offerStatus : 'DRAFT',
						offerLine2.product ? offerLine2.product.offerStatus : 'DRAFT'
					)
				}),
				new NumericColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.wizard.table.header.quantity'),
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product) {
							return '';
						}
						return offerLine.product.quantity !== undefined
							? offerLine.product.quantity
							: UNDERSCORE;
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareNbr(
						offerLine1.product ? offerLine1.product.quantity : undefined,
						offerLine2.product ? offerLine2.product.quantity : undefined
					)
				}),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.price'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product || !offerLine.header.currency) {
							return undefined;
						}
						if (offerLine.product.price === undefined) {
							return UNDERSCORE;
						}
						const currencyPlusUnit = offerLine.header.pricingUnit === undefined
							? String(offerLine.header.currency)
							: String(offerLine.header.currency)
								.concat('/')
								.concat(String(offerLine.header.pricingUnit).toLowerCase());

						return formatNumber(
							offerLine.product.price,
							'amount',
							currencyPlusUnit.trim()
						);
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareNbr(
						offerLine1.product ? offerLine1.product.price : undefined,
						offerLine2.product ? offerLine2.product.price : undefined
					)
				}),

				// product part
				new LinkColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.product'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product?.description) {
							return undefined;
						}
						const { description } = offerLine.product;
						return description.configurationRef || description.productRef;
					},
					cellRendererParams: linkToProductCardCellRendererParams,
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => {
						const d1 = offerLine1.product ? offerLine1.product.description : undefined;
						const d2 = offerLine2.product ? offerLine2.product.description : undefined;
						return compareStr(
							d1
								? d1.configurationRef || d1.productRef
								: undefined,
							d2
								? d2.configurationRef || d2.productRef
								: undefined
						);
					}
				}),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.subcategory'),
					minWidth: 200,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product?.description?.family) {
							return undefined;
						}
						return offerLine.product.description.family.subCategory;
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareStr(
						offerLine1.product?.description?.family
							? offerLine1.product.description.family.subCategory : undefined,
						offerLine2.product?.description?.family
							? offerLine2.product.description.family.subCategory : undefined
					)
				}),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.form'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product?.description?.family) {
							return undefined;
						}
						return offerLine.product.description.family.form;
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareStr(
						offerLine1.product?.description?.family
							? offerLine1.product.description.family.form : undefined,
						offerLine2.product?.description?.family
							? offerLine2.product.description.family.form : undefined
					)
				}),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.table.header.steelGrade'),
					minWidth: 300,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const offerLine = params.data;
						if (!offerLine.product?.description) {
							return undefined;
						}
						return offerLine.product.description.steelGrade;
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareStr(
						offerLine1.product?.description
							? offerLine1.product.description.steelGrade : undefined,
						offerLine2.product?.description
							? offerLine2.product.description.steelGrade : undefined
					)
				}),
				new CommonColDefWithCustomComparator<OfferLine>({
					headerName: this.translationService.getTR('eoffer.table.header.dimensions'),
					minWidth: 200,
					valueGetter: params => {
						if (!params.data) {
							return undefined;
						}
						const offerLine = params.data;
						if (!offerLine.product?.description?.dimension) {
							return undefined;
						}
						const { dimension } = offerLine.product.description;
						return formatTextDimension(
							`${ dimension.thickness.value }\u00A0${ dimension.thickness.unit }`,
							`${ dimension.width.value }\u00A0${ dimension.width.unit }`,
							dimension.length
								? `${ dimension.length.value }\u00A0${ dimension.length.unit }`
								: undefined
						);
					},
					customComparator: (offerLine1: OfferLine, offerLine2: OfferLine) => compareDimension(
						offerLine1.product?.description
							? offerLine1.product.description.dimension : undefined,
						offerLine2.product?.description
							? offerLine2.product.description.dimension : undefined
					)
				})
			],
		});
	}

	onGridReady(params: GridReadyEvent) {
		this.offerLineDatasource = new OfferLineDatasource(this.offerService, params.api, this.type);
		this.dataSub = this.offerLineDatasource.subscribeToRowsCount({
			next: value => {
				this.offerLineCount = value;
			}
		});
		params.api.setGridOption('serverSideDatasource', this.offerLineDatasource);
		params.api.sizeColumnsToFit();
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		this.filterSub = this.filterObs
			? this.filterObs.subscribe({
				next: filter => {
					if (this.offerLineDatasource) {
						this.offerLineDatasource.filter = filter;
					}
					params.api.onFilterChanged();
				},
				error: (err: unknown) => {
					console.error('unable listen status filter', err);
				}
			})
			: undefined;
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	ngOnDestroy() {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
		if (this.filterSub !== undefined) {
			this.filterSub.unsubscribe();
		}
		if (this.dataSub !== undefined) {
			this.dataSub.unsubscribe();
		}
	}
}
